import React, { useState } from "react"
import { AdderTypes, dollarsToNum, dollars, IEquipmentOption, EquipmentType, LoanCalculations, Adders } from "@oneethos/shared"
import { usePpw } from "../hooks"
import { getSystemLabels } from "../lib/teslaSystemDetailsLabels"
import { NumericInput, DollarsInput, TextInput } from "./forms"
import './system-detail-inputs.scss'
import { FaTrash } from "react-icons/fa"
import { FastTrackFields } from './'
import TotalCostsSummary from './total-costs-summary'

type SystemDetailInputsProps = {
  formData: any
  handleChange: (formData: any) => void
  selectedEquipment: Partial<IEquipmentOption> | null
  isResidential: boolean
  disabled?: boolean
  actionButtons?: React.ComponentType<{ isValid: boolean }>
  showValidation?: boolean
  isValidExternal?: boolean
}

export const SystemDetailInputs = ({
  formData,
  handleChange,
  disabled,
  selectedEquipment,
  isResidential,
  showValidation,
  isValidExternal = true,
  actionButtons
}: SystemDetailInputsProps) => {
  const { ppwPV, ppwStorage } = usePpw(formData)
  const [hasTouchedSystemSize, setHasTouchedSystemSize] = useState(ppwPV !== undefined)

  const systemLabels = getSystemLabels(selectedEquipment)
  const adders = new Adders(formData.adders)
  const customAdders = adders.getCustomAdders()

  const PPW_THRESHOLD = 3.75
  const showPpwWarning = !selectedEquipment?.isTesla
    && hasTouchedSystemSize
    && ppwPV > PPW_THRESHOLD

  const minimumAmount = isResidential ? 10000 : 25000
  const storagePrice = formData.adders?.find(a => a.description === AdderTypes.StoragePrice)?.amount
  const systemCost = LoanCalculations.calcSystemCost({ ...formData, adders: adders.getList() })
  const loanAmount = LoanCalculations.calcLoanAmount({ ...formData, adders: adders.getList() })

  const isStorageOnly = selectedEquipment?.id === EquipmentType.StorageOnly
  const isTeslaResidential = selectedEquipment?.isTesla && isResidential
  
  // Validation logic consolidated from PaymentCalculator
  const systemSizeIsValid = formData.systemSizeKW > 0
  const storageCapacityIsValid = formData.storageCapacity > 0
  const downpaymentIsValid = !formData.adders?.some(adder => (
    adder.description === AdderTypes.Downpayment &&
    dollarsToNum(adder.amount) >= dollarsToNum(systemCost)
  ))
  const solarCostIsValid = dollarsToNum(formData.solarCost) >= minimumAmount
  const loanAmountIsValid = dollarsToNum(loanAmount) >= minimumAmount
  const storagePriceIsValid = dollarsToNum(storagePrice) >= minimumAmount
  const teslaPowerwallQtyIsValid = formData.teslaPowerwallQty > 0
  const storageQtyIsValid = formData.storageQty > 0
  const hasStorageBeingUsed = dollarsToNum(storagePrice) > 0 || formData.storageQty > 0

  // Commercial/Non-profit specific validation
  const commercialValidationIsValid = isResidential || (
    dollarsToNum(formData.avgMonthlyUtilityBill) > 0 &&
    dollarsToNum(formData.estAvgMonthlyUtilityBillAfterSolar) >= 0 &&
    dollarsToNum(formData.estAvgMonthlyUtilityBillAfterSolar) < dollarsToNum(formData.avgMonthlyUtilityBill)
  )

  const getEquipmentValidation = (): boolean => {
    const commonValidations = isValidExternal && downpaymentIsValid && loanAmountIsValid

    switch (selectedEquipment?.id) {
      case EquipmentType.StorageOnly:
        return commonValidations &&
          storagePriceIsValid &&
          storageCapacityIsValid &&
          storageQtyIsValid

      case EquipmentType.TeslaRoof:
      case EquipmentType.TeslaSolar:
        return commonValidations &&
          solarCostIsValid &&
          systemSizeIsValid &&
          (!hasStorageBeingUsed || (storageCapacityIsValid && teslaPowerwallQtyIsValid))

      case EquipmentType.StandardSolar:
      default:
        return commonValidations &&
          solarCostIsValid &&
          systemSizeIsValid &&
          commercialValidationIsValid &&
          (!hasStorageBeingUsed || storageCapacityIsValid)
    }
  }

  const displayCalculatePaymentBtn = getEquipmentValidation()

  const validationMessages = [
    !isStorageOnly && { isValid: solarCostIsValid, message: 'Please provide a valid Solar Price.' },
    !isStorageOnly && { isValid: systemSizeIsValid, message: 'Please provide a valid System Size.' },
    { isValid: loanAmountIsValid, message: `Loan amount must be at least ${dollars(minimumAmount)}.` },
    { isValid: !hasStorageBeingUsed || storageCapacityIsValid, message: 'Please provide a valid Storage Capacity.' },
    { isValid: commercialValidationIsValid, message: 'Please provide valid utility bill information for commercial/non-profit projects.' },
  ].filter(Boolean).filter(msg => !msg.isValid)

  return <div>
    <div className="system-details">
      <div className="system-prices">
        {systemLabels.hasSolarInput ?
          <div className="form-group">
            <label>{systemLabels.solarLabel}*</label>
            <DollarsInput
              placeholder="$10,000"
              disabled={disabled}
              extraValidator={(value) => {
                const solarAmount = dollarsToNum(value)
                return solarAmount >= minimumAmount ? '' : `The minimum solar price is ${dollars(minimumAmount)}`
              }}
              value={formData.solarCost}
              onChange={v => handleChange({
                ...formData,
                solarCost: v
              })}
            />
          </div> : null
        }
        {systemLabels.hasRoofInput ?
          <div className="form-group">
            <label>{AdderTypes.RoofPrice}</label>
            <DollarsInput
              placeholder="$0.00"
              emptyOk
              disabled={disabled}
              before="+"
              value={formData.adders?.find(a => a.description === AdderTypes.RoofPrice)?.amount}
              onChange={v => handleChange({
                ...formData,
                adders: [
                  ...(formData.adders?.filter(a => a.description !== AdderTypes.RoofPrice) || []),
                  { description: AdderTypes.RoofPrice, amount: v }
                ]
              })}
            />
          </div> : null
        }
        <div className='adders-container'>
          <div className="form-group">
            <label>{systemLabels.storageLabel}{isStorageOnly ? '*' : ''}</label>
            <DollarsInput
              placeholder="$0.00"
              emptyOk
              disabled={disabled}
              before="+"
              value={storagePrice}
              onChange={v => handleChange({
                ...formData,
                adders: [
                  ...(formData.adders?.filter(a =>
                    a.description !== AdderTypes.StoragePrice) || []),
                  {
                    description: AdderTypes.StoragePrice,
                    amount: v
                  }
                ]
              })}
              extraValidator={(value) => {
                const storageAmount = dollarsToNum(value)
                const storageQty = formData.storageQty
                if (storageAmount > 0 && !isStorageOnly && !isTeslaResidential && storageQty === undefined) return 'Storage Quantity is required'

                if (isTeslaResidential) {
                  return formData.teslaPowerwallQty > 0 ? '' : 'Powerwall Quantity is required'
                }

                if (isStorageOnly) {
                  if (storageAmount < 10000) {
                    return `Battery Total Price should be minimum ${dollars(10000)}`
                  }
                  return storageQty > 0 ? '' : 'Battery Quantity is required'
                }

                return ''
              }}

            />
          </div>
          {systemLabels.hasPowerwallQtyInput ?
            <div className="form-group">
              <label>Powerwall Quantity</label>
              <NumericInput
                emptyOk
                placeholder="0"
                disabled={disabled}
                value={formData.teslaPowerwallQty}
                onChange={v => {
                  handleChange({
                    ...formData,
                    teslaPowerwallQty: v
                  })
                }}
                extraValidator={(value) => {
                  const numValue = parseInt(value) || 0
                  const storageAmount = dollarsToNum(formData.adders?.find(a =>
                    a.description === AdderTypes.StoragePrice)?.amount)

                  if (numValue === 0) {
                    return storageAmount === 0 ? '' : 'Powerwall Quantity is required'
                  }

                  return storageAmount > 0 ? '' : 'Powerwall Total Price is required'
                }}
              />
            </div> : null
          }
          {isStorageOnly || (systemLabels.hasStorageQtyInput && dollarsToNum(storagePrice) > 0) ?
            <div className="form-group">
              <label>{isStorageOnly ? 'Battery Quantity*' : 'Storage Quantity*'}</label>
              <NumericInput
                emptyOk
                placeholder="0"
                disabled={disabled}
                value={formData.storageQty}
                onChange={v => {
                  handleChange({
                    ...formData,
                    storageQty: v
                  })
                }}
                extraValidator={(value) => {
                  const numValue = parseInt(value) || 0
                  const storageAmount = dollarsToNum(storagePrice)

                  if (numValue === 0) {
                    return storageAmount === 0 ? '' : (isStorageOnly ? 'Battery Quantity is required' : 'Storage Quantity is required')
                  }

                  return storageAmount > 0 ? '' : (isStorageOnly ? 'Battery Total Price is required' : 'Storage Price is required')
                }}
              />
            </div> : null
          }
          {systemLabels.hasHvacInput ?
            <div className="form-group">
              <label>{AdderTypes.HvacPrice}</label>
              <DollarsInput
                placeholder="$0.00"
                emptyOk
                disabled={disabled}
                value={formData.adders?.find(a => a.description === AdderTypes.HvacPrice)?.amount}
                before="+"
                onChange={v => handleChange({
                  ...formData,
                  adders: [
                    ...(formData.adders?.filter(a =>
                      a.description !== AdderTypes.HvacPrice) || []),
                    { description: AdderTypes.HvacPrice, amount: v }
                  ]
                })}
              />
            </div> : null
          }
          {customAdders.map(customAdder => (
            <div key={customAdder.description} className="form-group">
              <label>{customAdder.description}</label>
              <DollarsInput
                placeholder="$0.00"
                emptyOk
                disabled={disabled}
                value={formData.adders?.find(a => a.description === customAdder.description)?.amount}
                before="+"
                after={
                  <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent"
                    onClick={() => handleChange({
                      ...formData,
                      adders: formData.adders?.filter(a =>
                        a.description !== customAdder.description) || []
                    })}
                    disabled={disabled}
                    style={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      lineHeight: '1',
                      display: 'flex',
                      width: 'auto',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    aria-label={`Remove ${customAdder.description}`}
                  >
                    <FaTrash />
                  </button>
                }
                onChange={v => handleChange({
                  ...formData,
                  adders: [
                    ...(formData.adders?.filter(a =>
                      a.description !== customAdder.description) || []),
                    { description: customAdder.description, amount: v }
                  ]
                })}
              />
            </div>
          ))}
          <div className="form-group">
            <label>{AdderTypes.Other} <sup>†</sup></label>
            <DollarsInput
              placeholder="$0.00"
              emptyOk
              disabled={disabled}
              value={formData.adders?.find(a => a.description === AdderTypes.Other)?.amount}
              before="+"
              onChange={v => handleChange({
                ...formData,
                adders: [
                  ...(formData.adders?.filter(a =>
                    a.description !== AdderTypes.Other) || []),
                  { description: AdderTypes.Other, amount: v }
                ]
              })}
            />
          </div>
        </div>
        <TotalCostsSummary
          systemCost={dollars(systemCost)}
          loanAmount={dollars(loanAmount)}
          formData={formData}
          handleChange={handleChange}
          disabled={disabled}
        />
        {!isResidential ? <FastTrackFields
          formData={formData}
          handleChange={handleChange}
          disabled={disabled}
        /> : null}
      </div>
      <div className="system-size">
        {systemLabels.hasSystemSizeInput ? (
          <div className="form-group">
            <label>{systemLabels.systemSizeKW}*</label>
            <NumericInput
              disabled={disabled}
              value={formData.systemSizeKW}
              onBlur={() => setHasTouchedSystemSize(true)}
              onChange={v => handleChange({
                ...formData,
                systemSizeKW: v
              })}
            />
          </div>
        ) : null}
        {!isStorageOnly &&
          <div className="form-group">
            <label>{systemLabels.pricePerWatt}</label>
            <TextInput
              disabled={true}
              value={systemLabels.pricePerWatt === 'Price Per Watt Hour'
                ? (ppwStorage ? dollars(ppwStorage) : '')
                : ppwPV ? dollars(ppwPV) : ''
              }
            />
          </div>}
          {(systemLabels.hasStorageCapacityInput && dollarsToNum(storagePrice) > 0) ? (
          <div className="form-group">
            <label>Storage Capacity (kWh)*</label>
            <NumericInput
              disabled={disabled}
              value={formData.storageCapacity}
              onChange={v => handleChange({
                ...formData,
                storageCapacity: v
              })}
            />
          </div>
        ) : null}
        {systemLabels.otherContent}
        <div className="mobile-totals">
          <TotalCostsSummary
            systemCost={dollars(systemCost)}
            loanAmount={dollars(loanAmount)}
            formData={formData}
            handleChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
    {showPpwWarning && (
      <div className="alert alert-danger">
        Loan applications with PPW higher than ${PPW_THRESHOLD} are unlikely to be approved.
      </div>
    )}

    {/* Validation messages */}
    {showValidation && document.activeElement.tagName !== 'INPUT' && validationMessages.length > 0 && (
      <div className="alert alert-info">
        {validationMessages.map((error, index) => (
          <div key={index}>{error.message}</div>
        ))}
      </div>
    )}

    {/* Action buttons */}
    {actionButtons && React.createElement(actionButtons, { isValid: displayCalculatePaymentBtn })}
  </div>
}

export default SystemDetailInputs